<template>
  <div class="system-info-container">
    <!-- Modern Dashboard Cards - 2x2 Grid -->
    <div class="row g-2 mb-3">
      <!-- Power Status Card -->
      <div class="col-12 col-sm-6 col-lg-6">
        <div class="status-card power-card">
          <div class="status-icon power-icon">
            <i class="fas fa-bolt"></i>
          </div>
          <div class="status-content">
            <div class="status-count">
              <span class="count-value">{{ activePowerCount }}</span>
              <span class="count-unit">/ {{ powerCount }}</span>
            </div>
            <div class="status-state">
              State: <span :class="powerStateClass">{{ powerState }}</span>
            </div>
            <div class="status-label">Power</div>
            <div class="status-details" v-if="systemInfo?.Power && systemInfo.Power.length > 0">
              <div 
                v-for="(pwr, index) in systemInfo.Power" 
                :key="index" 
                class="status-item"
              >
                <span class="status-item-label">P{{ pwr.Number }}:</span>
                <span :class="pwr.Status && pwr.PresenceStatus ? 'text-success' : 'text-danger'">
                  {{ pwr.PresenceStatus ? (pwr.Status ? 'OK' : 'Fail') : 'Absent' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fan Status Card -->
      <div class="col-12 col-sm-6 col-lg-6">
        <div class="status-card fan-card">
          <div class="status-icon fan-icon">
            <i class="fas fa-fan"></i>
          </div>
          <div class="status-content">
            <div class="status-count">
              <span class="count-value">{{ activeFanCount }}</span>
              <span class="count-unit">/ {{ fanCount }}</span>
            </div>
            <div class="status-state">
              State: <span :class="fanStateClass">{{ fanState }}</span>
            </div>
            <div class="status-label">FAN</div>
            <div class="status-details" v-if="systemInfo?.Fan && systemInfo.Fan.length > 0">
              <div 
                v-for="(fan, index) in systemInfo.Fan" 
                :key="index" 
                class="status-item"
              >
                <span class="status-item-label">F{{ fan.Number }}:</span>
                <span :class="fan.Status ? 'text-success' : 'text-danger'">
                  {{ fan.Speed }} RPM ({{ fan.Status ? 'OK' : 'Fail' }})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CPU and Memory Card -->
      <div class="col-12 col-sm-6 col-lg-6">
        <div class="status-card cpu-memory-card">
          <div class="cpu-memory-container">
            <CircularProgress
              :percentage="cpuUsage"
              label="CPU"
              :size="80"
              color="purple"
            />
            <CircularProgress
              :percentage="ramUsage"
              label="Memory"
              :size="80"
              color="purple"
            />
          </div>
        </div>
      </div>

      <!-- Temperature Card -->
      <div class="col-12 col-sm-6 col-lg-6">
        <div class="status-card temperature-card flex-column text-center p-5">
          <div class="status-icon temperature-icon ml-3">
            <i class="fas fa-thermometer-half"></i>
          </div>
          <div class="status-content">
            <div class="temperature-value">{{ temperature }}°C</div>
            
            <div class="status-state">Temperature</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Information Section - Previous UI Style -->
    <div class="detailed-info-section">
      
      <div  class="info-content">
        <!-- Device Model -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Device Model:</span>
          <span class="text-sm">{{ systemInfo?.model || 'N/A' }}</span>
        </div>

        <!-- System Name -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">System Name:</span>
          <span class="text-sm">{{ systemInfo?.sys_name || 'N/A' }}</span>
        </div>

        <!-- Vendor -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Vendor:</span>
          <span class="text-sm">{{ systemInfo?.vendor || 'N/A' }}</span>
        </div>

        <!-- Serial Number -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Serial Number:</span>
          <span class="text-sm">{{ systemInfo?.serial || 'N/A' }}</span>
        </div>

        <!-- Hardware Version -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Hardware Version:</span>
          <span class="text-sm">{{ systemInfo?.hardware || 'N/A' }}</span>
        </div>

        <!-- Firmware Version -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Firmware Version:</span>
          <span class="text-sm">{{ systemInfo?.firmware || 'N/A' }}</span>
        </div>

        <!-- Web Version -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Web Version:</span>
          <span class="text-sm">{{ systemInfo?.web_version || 'N/A' }}</span>
        </div>

        <!-- Device MAC -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Device MAC:</span>
          <span class="text-sm">{{ systemInfo?.device_mac || 'N/A' }}</span>
        </div>

        <!-- Management MAC -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Management MAC:</span>
          <span class="text-sm">{{ systemInfo?.mgmt_mac || 'N/A' }}</span>
        </div>

        <!-- Device Type -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Device Type:</span>
          <span class="text-sm">{{ systemInfo?.device_type || 'N/A' }}</span>
        </div>

        <!-- CPU Usage -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">CPU Usage:</span>
          <span class="text-sm">{{ systemInfo?.CpuUsage || 'N/A' }}%</span>
        </div>

        <!-- RAM Usage -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">RAM Usage:</span>
          <span class="text-sm">{{ systemInfo?.RamUsage || 'N/A' }}%</span>
        </div>

        <!-- Temperature -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Temperature:</span>
          <span class="text-sm">{{ systemInfo?.Temperature || 'N/A' }}°C</span>
        </div>

        <!-- Device Time -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Device Time:</span>
          <span class="text-sm">{{ formatTimestamp(systemInfo?.device_time) }}</span>
        </div>

        <!-- Uptime -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">Uptime:</span>
          <span class="text-sm">{{ formatUptime(systemInfo?.uptime_seconds) }}</span>
        </div>

        <!-- CSM -->
        <div class="mb-1 d-flex justify-content-between">
          <span class="text-sm font-weight-bold">CSM:</span>
          <span class="text-sm">{{ systemInfo?.csm || 'N/A' }}</span>
        </div>

        
        

        <!-- Reboot History -->
        <div v-if="systemInfo?.raw?.DeviceRebootReasonList?.length" class="mt-3">
          <h6 class="text-info">Reboot History</h6>

          <div v-for="(reboot, index) in systemInfo.raw.DeviceRebootReasonList.slice(0, 3)"
               :key="index"
               class="mb-1 d-flex justify-content-between">

            <span class="text-sm font-weight-bold">{{ reboot.RebootReasonNote }}</span>
            <span class="text-sm">{{ formatTimestamp(reboot.RebootReasonTIme) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CircularProgress from './CircularProgress.vue';

export default {
  name: 'GponClodeSystemInfo',
  components: {
    CircularProgress
  },
  props: {
    systemInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showDetails: false
    }
  },
  computed: {
    powerCount() {
      return this.systemInfo?.Power?.length || 0;
    },
    activePowerCount() {
      if (!this.systemInfo?.Power || this.systemInfo.Power.length === 0) return 0;
      return this.systemInfo.Power.filter(p => p.Status && p.PresenceStatus).length;
    },
    powerState() {
      if (!this.systemInfo?.Power || this.systemInfo.Power.length === 0) return 'N/A';
      const allOk = this.systemInfo.Power.every(p => p.Status && p.PresenceStatus);
      return allOk ? 'Running' : 'Warning';
    },
    powerStateClass() {
      return this.powerState === 'Running' ? 'text-success' : 'text-warning';
    },
    fanCount() {
      return this.systemInfo?.Fan?.length || 0;
    },
    activeFanCount() {
      if (!this.systemInfo?.Fan || this.systemInfo.Fan.length === 0) return 0;
      return this.systemInfo.Fan.filter(f => f.Status).length;
    },
    fanState() {
      if (!this.systemInfo?.Fan || this.systemInfo.Fan.length === 0) return 'N/A';
      const allOk = this.systemInfo.Fan.every(f => f.Status);
      return allOk ? 'Running' : 'Warning';
    },
    fanStateClass() {
      return this.fanState === 'Running' ? 'text-success' : 'text-warning';
    },
    cpuUsage() {
      const usage = parseInt(this.systemInfo?.CpuUsage) || 0;
      return isNaN(usage) ? 0 : Math.min(Math.max(usage, 0), 100);
    },
    ramUsage() {
      const usage = parseInt(this.systemInfo?.RamUsage) || 0;
      return isNaN(usage) ? 0 : Math.min(Math.max(usage, 0), 100);
    },
    temperature() {
      return this.systemInfo?.Temperature || 'N/A';
    }
  },
  methods: {
    formatUptime(seconds) {
      if (!seconds) return 'N/A';

      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      if (days > 0) return `${days}d ${hours}h ${minutes}m`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A';
      return new Date(timestamp * 1000).toLocaleString();
    }
  }
}
</script>

<style scoped>
.system-info-container {
  padding: 0;
}

/* Status Cards */
.status-card {
  background: linear-gradient(135deg, rgba(12, 28, 53, 0.9) 0%, rgba(20, 40, 70, 0.9) 100%);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  min-height: 100px;
}

.status-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.status-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.power-icon {
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(243, 156, 18, 0.3);
}

.fan-icon {
  background: linear-gradient(135deg, #1abc9c 0%, #16a085 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(26, 188, 156, 0.3);
}

.temperature-icon {
  background: linear-gradient(135deg, #1abc9c 0%, #16a085 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(26, 188, 156, 0.3);
}

.status-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.status-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-wrap: wrap;
}

.count-value {
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: bold;
  color: #2ecc71;
  line-height: 1;
}

.count-unit {
  font-size: clamp(9px, 1.2vw, 11px);
  color: rgba(255, 255, 255, 0.7);
}

.status-state {
  font-size: clamp(9px, 1.2vw, 11px);
  color: rgba(255, 255, 255, 0.8);
}

.status-label {
  font-size: clamp(9px, 1.2vw, 11px);
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.2px;
  margin-top: 1px;
}

.status-details {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: clamp(8px, 1vw, 10px);
  gap: 4px;
  line-height: 1.2;
}

.status-item-label {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  flex-shrink: 0;
}

.temperature-value {
  font-size: clamp(22px, 4vw, 28px);
  font-weight: bold;
  color: white;
  line-height: 1;
}

/* CPU/Memory Card */
.cpu-memory-card {
  justify-content: space-around;
  padding: 12px;
  min-height: 120px;
}

.cpu-memory-container {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-wrap: wrap;
}

/* Detailed Information Section */
.detailed-info-section {
  background: rgba(12, 28, 53, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin-top: 20px;
}

.info-header {
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.info-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.info-header h6 {
  color: white;
  font-weight: 600;
  margin: 0;
}

.info-header i {
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.3s;
}

.info-content {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Responsive Design */
@media (max-width: 991.98px) {
  .status-card {
    padding: 8px;
    gap: 8px;
    min-height: 95px;
  }

  .status-icon {
    width: 35px;
    height: 35px;
    font-size: 15px;
  }

  .cpu-memory-card {
    padding: 10px;
    min-height: 110px;
  }

  .cpu-memory-container {
    gap: 10px;
  }
}

@media (max-width: 767.98px) {
  .status-card {
    padding: 8px;
    gap: 8px;
    min-height: 90px;
  }

  .status-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .cpu-memory-card {
    padding: 10px;
    min-height: 180px;
  }

  .cpu-memory-container {
    flex-direction: column;
    gap: 15px;
  }

  .status-content {
    gap: 4px;
  }

  .info-content {
    padding: 16px;
  }
}

@media (max-width: 575.98px) {
  .status-card {
    padding: 8px;
    gap: 8px;
    min-height: 85px;
  }

  .status-icon {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }

  .cpu-memory-card {
    padding: 12px;
    min-height: 220px;
  }

  .info-content {
    padding: 12px;
  }

  .info-header {
    padding: 12px 16px;
  }
}

/* Extra small devices */
@media (max-width: 375px) {
  .status-card {
    flex-direction: column;
    text-align: center;
    padding: 16px;
    min-height: auto;
  }

  .status-content {
    align-items: center;
  }

  .cpu-memory-container {
    gap: 15px;
  }
}
</style>